import {
  countDataPoints,
  type SectionNode,
} from '@/app/(client)/network/-components/ProfileSidebar';
import type {
  DataPointSearchResult,
  ImpactArea,
  ProfileTemplate,
} from '@/app/(client)/network/-types';

export function findFirstLeafTemplateId(
  areas: ImpactArea[],
  validIds: Set<string>,
): string | null {
  for (const area of areas) {
    if (area.templateId && validIds.has(area.templateId))
      return area.templateId;
    if (area.children) {
      const found = findFirstLeafTemplateId(area.children, validIds);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Leaf template ids in the exact order the sidebar tree renders them
 * (depth-first through the impact-area hierarchy). Used to order the
 * cross-template search result sections so they match the sidebar.
 */
export function collectLeafTemplateIds(
  areas: ImpactArea[],
  validIds: Set<string>,
): string[] {
  const result: string[] = [];
  for (const area of areas) {
    if (area.templateId) {
      if (validIds.has(area.templateId)) result.push(area.templateId);
    } else {
      result.push(...collectLeafTemplateIds(area.children ?? [], validIds));
    }
  }
  return result;
}

/**
 * Groups data-point search hits by the template they belong to.
 *
 * A hit is not necessarily a data-point *name* match — the server also
 * returns every data point of a template whose *template name* matches the
 * query (e.g. searching "wage" matches the "Wages & Benefits" category), so
 * a template can legitimately come back with all of its data points.
 */
export function groupSearchIdsByTemplate(
  results: DataPointSearchResult[],
): Record<string, Set<string>> {
  const grouped: Record<string, Set<string>> = {};
  for (const result of results) {
    const ids = grouped[result.templateId] ?? new Set<string>();
    ids.add(result.retracedId);
    grouped[result.templateId] = ids;
  }
  return grouped;
}

/**
 * @param selectedId Template the content pane is showing. Callers pass `null`
 *   while search results are on screen so no leaf is marked active — the
 *   matched leaves carry the highlight instead (DELTA-2461). The selection
 *   itself (the `?esgTemplate=` param) is untouched, only its highlight.
 * @param matchedTemplateIds Templates holding hits for the active search —
 *   their leaves get highlighted so the sidebar shows where the matches live.
 *   Pass an empty set when no search is active (parents are never marked,
 *   matching the old FE).
 */
export function buildEsgSections(
  areas: ImpactArea[],
  validIds: Set<string>,
  loaded: Record<string, ProfileTemplate>,
  selectedId: string | null,
  onSelect: (id: string) => void,
  matchedTemplateIds: Set<string>,
): SectionNode[] {
  const result: SectionNode[] = [];
  for (const area of areas) {
    if (area.templateId) {
      if (!validIds.has(area.templateId)) continue;
      const tmpl = loaded[area.templateId];
      const { filled, total } = tmpl
        ? countDataPoints(tmpl.data)
        : { filled: 0, total: 0 };
      result.push({
        id: area.id,
        label: area.name,
        filled,
        total,
        isActive: area.templateId === selectedId,
        isMatched: matchedTemplateIds.has(area.templateId),
        onClick: () => onSelect(area.templateId!),
      });
    } else {
      const children = buildEsgSections(
        area.children ?? [],
        validIds,
        loaded,
        selectedId,
        onSelect,
        matchedTemplateIds,
      );
      if (!children.length) continue;
      const filled = children.reduce((s, c) => s + c.filled, 0);
      const total = children.reduce((s, c) => s + c.total, 0);
      result.push({ id: area.id, label: area.name, filled, total, children });
    }
  }
  return result;
}
